import {
    Component, DestroyRef, inject, Input, ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { merge, startWith } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { SentryService } from 'src/app/services/sentry.service';
import { FileSelectorComponent, UploadedFileType } from '../file-selector/file-selector.component';
import { NgFor, NgClass, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TooltipOnOverflowDirective } from '../../../directives/tooltip-on-overflow.directive';
import { MatIconButton } from '@angular/material/button';
import { CanViewFileInBrowserPipe } from '../../../pipes/can-view-file-in-browser.pipe';

@Component({
    selector: 'file-list',
    templateUrl: './file-list.component.html',
    styleUrl: './file-list.component.scss',
    imports: [
        NgFor,
        NgClass,
        NgIf,
        MatIcon,
        MatTooltip,
        TooltipOnOverflowDirective,
        MatIconButton,
        MatPaginator,
        CanViewFileInBrowserPipe,
    ],
})
export class FileListComponent {
    /**
     * Reference to paginator component
    */
    @ViewChild(MatPaginator) paginator?: MatPaginator;

    /**
     * Reference to file selector component.
     * If provided this component automatically reflects changes of the selected files.
     */
    @Input() fileSelector?: FileSelectorComponent;

    /**
     * Control tracking the files that should be displayed in the list
     */
    @Input() control?: FormControl<UploadedFileType[]>;

    /**
     * Show a delete button. Note that this does not actually delete a file, it simply removes it from the value
     * of the control. Actual file deletion should be handled elsewhere.
     */
    @Input() delete = false;

    /**
     * Show a view button (opens the file in a new tab)
     */
    @Input() view = true;

    /**
     * Show a download button
     */
    @Input() download = true;

    /**
     * Filtered files based on paginator
     */
    filteredFiles: UploadedFileType[] = [];

    /**
     * DestroyRef
     */
    destroyRef = inject(DestroyRef);

    /**
     * Currently selected order by column
     */
    orderColumn: 'created_at' | 'title' | 'status' = 'created_at';

    /**
     * Currently selected order by direction
     */
    orderDirection: 'asc' | 'desc' = 'desc';

    /**
     * Store total amount of files in the file-list so we can detect when a new file has been uploaded
     */
    private previousFileCount = 0;

    constructor(private api: ApiService, private snackbar: SnackbarService, private sentry: SentryService) { }

    /**
     * Start tracking control and paginator values after view init
     */
    ngAfterViewInit(): void {
        // Create a new control to track values if one was not provided via input
        if (!this.control) {
            this.control = new FormControl<{ file: File; id?: string; status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry'; }[]>(
                [],
                { nonNullable: true },
            );
        }

        // Reflect fileSelector selection changes into the control
        if (this.fileSelector) {
            this.fileSelector.filesSelected.subscribe((files) => {
                this.control?.setValue(files);
            });
        }

        // Listen to control and paginator changes in order to update the filtered files
        if (this.paginator) {
            merge(
                this.control.valueChanges,
                this.paginator.page,
            ).pipe(
                takeUntilDestroyed(this.destroyRef),
                startWith(undefined),
            ).subscribe(() => {
                // Unfortunately this code has to run in a timeout to prevent "value changed after checking" errors.
                setTimeout(() => {
                    if (this.paginator && this.control) {
                        // Ensure queued files appear at the top of the list
                        const allFiles = [...this.control.value];

                        // Amount of files currently in the formcontrol
                        const currentFileCount = this.control.value.length;

                        // When a new file is added, they appear at the top
                        if (currentFileCount > this.previousFileCount) {
                            this.switchOrder('status', 'asc');
                        }
                        this.previousFileCount = currentFileCount;

                        this.filteredFiles = allFiles.slice(
                            this.paginator.pageIndex * this.paginator.pageSize,
                            (this.paginator.pageIndex + 1) * this.paginator.pageSize,
                        );
                        this.paginator.length = allFiles.length;
                    }
                    if (this.fileSelector && this.control?.value) {
                        this.fileSelector.files = this.control.value;
                    }
                });
            });
        }
    }

    /**
     * Switch to a different column to order on
     * @param column The column that should be used for ordering
     * @param directionString direction of the sorting (ascending or descending)
     */
    switchOrder(column?: typeof this.orderColumn, directionString?: typeof this.orderDirection): void {
        if (!this.control) {
            return;
        }
        // Use current sorting config if new one was not provided
        if (!column) {
            column = this.orderColumn;
        }
        if (!directionString) {
            directionString = this.orderDirection;
        }
        const now = new Date();
        // Custom priority order for status ascending: failed -> processing -> available
        const statusOrder: Record<string, number | undefined> = { failed: 0, processing: 1, available: 2 };
        // Functions that help with access the correct property from a column string
        const columnAccessors: Record<string, (row: any) => any> = {
            title: (row) => row.file.name.toLowerCase(),
            status: (row) => statusOrder[row.status] ?? Number.MAX_SAFE_INTEGER,
            created_at: (row) => row.created_at ?? now,
        };
        // Retrieve getter function from the columnAccessors
        const getValue = columnAccessors[column];

        // Sorting logic
        // Multiply by directionsign to go ascending or descending
        const directionSign = directionString === 'asc' ? 1 : -1;
        const sorted = [...this.control.value].sort(
            (a, b) => {
                let comparisonResult = 0;
                if (getValue(a) > getValue(b)) {
                    comparisonResult = 1;
                } else if (getValue(a) < getValue(b)) {
                    comparisonResult = -1;
                }
                return directionSign * comparisonResult;
            },
        );
        this.control.setValue(sorted);

        // Store current sorting configuration
        this.orderColumn = column;
        this.orderDirection = directionString;
        this.paginator?.firstPage();
    }

    /**
     * Handle delete button press
     * @param index Index of the file in the currently displayed (paginated/filtered) list
     */
    deleteFile(index: number): void {
        if (!this.control) {
            return;
        }

        if (index > this.filteredFiles.length - 1) {
            return;
        }
        // Get the file from the filtered list
        const fileToDelete = this.filteredFiles[index];

        // Remove it from the control's value
        const originalIndex = this.control.value.findIndex((file) => file.id === fileToDelete.id && file.file === fileToDelete.file);
        if (originalIndex === -1) {
            return;
        }

        this.control.value.splice(originalIndex, 1);
        this.control.setValue(this.control.value);
    }

    /**
     * Handle download button press
     * @param file File that was pressed
     */
    async downloadFile(file: { file: File; id?: string; status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry'; }): Promise<void> {
        try {
            if (this.paginator && file.id) {
                await this.api.downloadFile(file.id, file.file.name);
            }
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Bestand downloaden mislukt`);
        }
    }

    /**
     * Handle view button press
     * @param file File that was pressed
     */
    async viewFile(file: { file: File; id?: string; status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry'; }): Promise<void> {
        try {
            if (this.paginator && file.id) {
                const blob = (await this.api.retrieveFile(file.id)).data;
                // Open file in new tab
                window.open(window.URL.createObjectURL(blob));
            }
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Bestand openen mislukt`);
        }
    }

    /**
     * Functions used as trackByFunction in ngFor loop in template
     * @param index Index of file
     * @param file File
     * @returns Id of file or name of not found
     */
    fileByIdOrName(index: number, file: { file: File; id?: string; status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry'; }): string {
        return file.id || file.file.name;
    }
}
